import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Close } from "@dreamhub/designsystem/icons";
import { IconButton } from "@/components/ui/icon-button";
import ToastBody from "@/components/ui/toast-body";
import { useAPIMutation } from "@/services/api/hook";
import { useAuth } from "@/services/auth/AuthContext";
import getApiEndpoint from "@/services/api/endpoint";
import { buildApiUrl } from "@/services/api/baseUrl";
import { Filters } from "@/services/api/types";
import { collapseNoValueFilters } from "@/services/api/noValueFilters";
import { SortingState } from "@tanstack/react-table";
import { SortDirection, ColumnData } from "@/entities/dataTables/types";
import { getVisibleExportColumns } from "@/entities/dataTables/helpers";
import { resolveSortField } from "@/entities/dataTables/sorting";
import { downloadBlob } from "@/entities/attachments/helpers";
import { extractFilenameFromContentDisposition } from "@/entities/attachments/hooks";
import { DataTableFilter } from "@/components/controls/DataTableFilters/DataTableFilters";
import { getEnumTKey } from "@/utils/strings";
import { tWithFallback } from "@/utils/i18n";
import { parseOptions } from "@/utils/entityFieldsUtils/shared";
import { getCurrentLocale } from "@/utils/locale";
import {
  getBrowserTimeZone,
  getExcelDateFormats
} from "@/utils/excelDateFormat";
import { useAnalytics } from "@/services/analytics/hook";
// Adaptive poll cadence: large exports cannot finish in the default 5-minute
// window, so we widen both interval and total budget based on expected row
// count. The warm-up delay on the largest tier avoids burning poll attempts
// during a phase where the job cannot possibly be done yet.
interface PollSchedule {
  intervalMs: number;
  maxAttempts: number;
  warmupMs: number;
}

const getPollSchedule = (totalRows?: number): PollSchedule => {
  if (totalRows === undefined || totalRows <= 5000) {
    return { intervalMs: 2000, maxAttempts: 150, warmupMs: 0 };
  }
  if (totalRows <= 50000) {
    return { intervalMs: 5000, maxAttempts: 180, warmupMs: 0 };
  }
  return { intervalMs: 10000, maxAttempts: 180, warmupMs: 30000 };
};

// Persisted job entries older than this are ignored on mount — by then the
// backend's poll window has lapsed and the job is either already finished or
// terminally stuck. Prevents an indefinite resume loop after a long absence.
const PERSISTED_JOB_TTL_MS = 35 * 60 * 1000;

// How long exportTo waits for columnsState to be seeded before giving up.
// Column data normally arrives well within a second of the table mounting.
const COLUMNS_READY_TIMEOUT_MS = 15 * 1000;
const COLUMNS_READY_POLL_MS = 250;

export type ExportFormat = "csv" | "xlsx";

// Pendo event fired once per export request, carrying the format and table.
const EXPORT_ANALYTICS_EVENT = "table_export";

// Error code the export service returns when the result exceeds what one
// Excel worksheet can hold; the user is pointed at filters or CSV instead.
const ROW_LIMIT_ERROR_CODE = "row_limit_exceeded";

interface ExportJobResponse {
  id: string;
  status: "queued" | "running" | "ready" | "failed";
  attachmentId?: string;
  errorCode?: string;
}

interface PersistedJob {
  jobId: string;
  createdAt: number;
  format?: ExportFormat;
}

// Custom single/multi-select fields store option ids, not their labels; the
// human-readable label lives in config.options. Delegate to the grid's own
// parseOptions so the export resolves ids to labels exactly as the grid does
// and the two can't drift. Array-form options are self-labeled (id === label),
// so the resulting map is a harmless identity for them and load-bearing only
// for object-form ({ id: name }).
const buildSelectOptionLabelMap = (
  options: unknown
): Record<string, string> => {
  const map: Record<string, string> = {};
  for (const { id, displayName } of parseOptions({ options })) {
    map[id] = displayName;
  }
  return map;
};

// Build the per-column value→label map for enum- and select-typed filter
// columns. Numeric enum FKs are no-ops at the backend (server-side *_resolved
// fold runs first); the map's load-bearing role is string-enum columns like
// salesExplorationStatus and custom single/multi-select fields (Deal Type,
// Rev. Share - Partner/Ext. BD) whose stored values are option ids with no FK
// lookup and would otherwise emit raw codes in CSV.
const buildColumnValueLabels = (
  filterOptions: DataTableFilter[] | undefined,
  visibleColumnIds: Set<string>,
  tFn: (key: string, fallback: string | null | undefined) => string
): Record<string, Record<string, string>> | undefined => {
  if (!filterOptions || filterOptions.length === 0) return undefined;
  const result: Record<string, Record<string, string>> = {};
  for (const option of filterOptions) {
    if (!visibleColumnIds.has(option.key)) continue;
    if (option.filterType.type === "enum") {
      const values = option.filterType.values as {
        enumType?: { [key: string]: string | number };
        entityType?: string;
        valueType?: string;
      };
      const { enumType, entityType, valueType } = values;
      if (!enumType || !entityType || !valueType) continue;
      const map: Record<string, string> = {};
      for (const enumKey of Object.keys(enumType)) {
        if (!isNaN(Number(enumKey))) continue;
        const rawValue = enumType[enumKey] as string | number;
        map[String(rawValue)] = tFn(
          getEnumTKey(entityType, valueType, enumType, rawValue as never),
          String(rawValue)
        );
      }
      if (Object.keys(map).length > 0) result[option.key] = map;
    } else if (option.filterType.type === "customField") {
      const map = buildSelectOptionLabelMap(option.filterType.values.options);
      if (Object.keys(map).length > 0) result[option.key] = map;
    }
  }
  return Object.keys(result).length > 0 ? result : undefined;
};

const storageKey = (statusRoute: string) => `export:inflight:${statusRoute}`;

const readPersistedJob = (statusRoute: string): PersistedJob | null => {
  try {
    const raw = localStorage.getItem(storageKey(statusRoute));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedJob;
    if (!parsed.jobId || typeof parsed.createdAt !== "number") return null;
    if (Date.now() - parsed.createdAt > PERSISTED_JOB_TTL_MS) {
      localStorage.removeItem(storageKey(statusRoute));
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const writePersistedJob = (
  statusRoute: string,
  jobId: string,
  format: ExportFormat
) => {
  try {
    localStorage.setItem(
      storageKey(statusRoute),
      JSON.stringify({ jobId, createdAt: Date.now(), format })
    );
  } catch {
    // localStorage may be unavailable (private mode, quota); resume is a
    // nice-to-have so swallow and continue with in-memory state only.
  }
};

const clearPersistedJob = (statusRoute: string) => {
  try {
    localStorage.removeItem(storageKey(statusRoute));
  } catch {
    // see writePersistedJob
  }
};

type XlsxOptions = {
  timeZone?: string;
  dateFormat: string;
  datetimeFormat: string;
  columnCurrencyFields?: Record<string, string>;
};

// Table-specific `FilterRequest` fields beyond filters and search, such as the
// Compare-to period. The export worker parses the snapshot back into the same
// FilterRequest the table reads with, so these travel camelCased.
export type ExtraFilterParams = Record<string, unknown>;

type ExportRequestBody = {
  filter: { filters: Filters; search?: { term: string } } & ExtraFilterParams;
  sort?: { field: string; direction: SortDirection }[];
  columns: string[];
  columnLabels: string[];
  columnValueLabels?: Record<string, Record<string, string>>;
  fullyResolved: true;
  format: ExportFormat;
  xlsxOptions?: XlsxOptions;
};

type BuildRequestBodyResult =
  | { status: "ready"; body: ExportRequestBody }
  | { status: "columns_not_seeded" }
  | { status: "no_visible_columns" };

// Excel formatting for the current browser: the user's zone so datetimes read
// as local wall-clock time, and date patterns in the locale's day/month order.
const buildXlsxOptions = (
  columnCurrencyFields: Record<string, string> | undefined
): XlsxOptions => {
  const timeZone = getBrowserTimeZone();
  const { dateFormat, datetimeFormat } = getExcelDateFormats(getCurrentLocale());
  return {
    ...(timeZone && { timeZone }),
    dateFormat,
    datetimeFormat,
    ...(columnCurrencyFields &&
      Object.keys(columnCurrencyFields).length > 0 && { columnCurrencyFields })
  };
};

export interface UseEntityExportOptions {
  createRoute: string;
  statusRoute: string;
  // Table identifier sent with the export analytics event (e.g. "deals").
  tableName: string;
  filters: Filters;
  sorting: SortingState;
  searchTerm?: string;
  columnsState: ColumnData[];
  totalRows?: number;
  // Lazy getter for filter metadata used to build the per-column value→label
  // map sent as `columnValueLabels`. A function (not a value) so callers can
  // reference a `useMemo` declared later in the same component body without
  // hitting TDZ — the getter is only invoked at export time. Backend
  // (csv_writer._resolve_cell) prefers this map over its default `format_cell`
  // for enum-typed columns whose raw DB value is a string like "not_booked"
  // with no FK lookup (e.g. salesExplorationStatus). FK columns are unaffected:
  // the server-side *_resolved fold runs first and overwrites the raw key with
  // the display value, so the str(raw) lookup here misses and falls through.
  getFilterOptions?: () => DataTableFilter[] | undefined;
  // Column IDs to drop from the export payload even when visible in the UI
  // (e.g. action columns like `alerts`, ID-only columns the backend has no
  // display value for, or composite UI-only columns).
  excludeFromExport?: readonly string[];
  // Map a UI column ID to a different backend field for the export only —
  // e.g. when the table cell renders `duration` via a formatter but the CSV
  // should send `durationDisplay` so the file is human-readable. UI labels
  // are preserved unchanged.
  exportColumnIdMap?: Readonly<Record<string, string>>;
  // Map a UI column ID to the field the backend sorts on, for columns whose id
  // isn't a sortable field (e.g. a composite date-range column sorts by its
  // start date). Must match what the table itself sends so the CSV comes out in
  // the order shown on screen.
  sortFieldByColumn?: Record<string, string>;
  // Extra per-column value→label mappings merged into the enum-derived map.
  // Use for columns whose UI label transformation isn't expressible as an enum
  // filter (e.g. SCO score badges like keyStakeholdersMappingText).
  getExtraColumnValueLabels?: () => Record<string, Record<string, string>> | undefined;
  // Excel only: money columns whose currency varies per row, mapped to the row
  // field holding that row's ISO currency code (e.g. the terms-currency half
  // of a split money pair → the row's `currency`). Lets the worksheet format
  // each cell in its own currency instead of the account default. Keys are UI
  // column IDs; only visible columns are sent.
  columnCurrencyFields?: Readonly<Record<string, string>>;
  // Extra fields merged into the request's `filter`, decided at export time
  // from the UI ids of the columns actually being exported — e.g. the
  // Compare-to period, sent only when a Δ column is in the file. They cannot
  // override `filters` or `search`.
  getExtraFilterParams?: (
    exportedColumnIds: ReadonlySet<string>
  ) => ExtraFilterParams | undefined;
  // Header labels keyed by UI column ID that replace the label snapshotted in
  // `columnsState`, for headers composed at render time from state the layout
  // does not track (e.g. a Δ header naming the selected Compare-to period).
  getColumnLabelOverrides?: () => Readonly<Record<string, string>> | undefined;
}

export const useEntityExport = ({
  createRoute,
  statusRoute,
  tableName,
  filters,
  sorting,
  searchTerm,
  columnsState,
  totalRows,
  getFilterOptions,
  excludeFromExport,
  exportColumnIdMap,
  sortFieldByColumn,
  getExtraColumnValueLabels,
  columnCurrencyFields,
  getExtraFilterParams,
  getColumnLabelOverrides
}: UseEntityExportOptions) => {
  const [isExporting, setIsExporting] = useState(false);
  const { token, tenantId, tokenExpiry } = useAuth();
  const { track } = useAnalytics();
  const trackRef = useRef(track);
  trackRef.current = track;
  const authRef = useRef({ token, tenantId, tokenExpiry });
  authRef.current = { token, tenantId, tokenExpiry };

  // Stable refs so useCallbacks don't churn on every render — mutation objects
  // and the filter-options getter are new references each render.
  const getFilterOptionsRef = useRef(getFilterOptions);
  getFilterOptionsRef.current = getFilterOptions;

  const getExtraColumnValueLabelsRef = useRef(getExtraColumnValueLabels);
  getExtraColumnValueLabelsRef.current = getExtraColumnValueLabels;

  const getExtraFilterParamsRef = useRef(getExtraFilterParams);
  getExtraFilterParamsRef.current = getExtraFilterParams;

  const getColumnLabelOverridesRef = useRef(getColumnLabelOverrides);
  getColumnLabelOverridesRef.current = getColumnLabelOverrides;

  const createMutation = useAPIMutation<object, ExportJobResponse>(
    createRoute,
    { method: "POST" }
  );
  const createMutationRef = useRef(createMutation);
  createMutationRef.current = createMutation;

  const pollMutation = useAPIMutation<
    { pathParams: { jobId: string } },
    ExportJobResponse
  >(statusRoute, { method: "GET" });
  const pollMutationRef = useRef(pollMutation);
  pollMutationRef.current = pollMutation;

  const buildRequestBody = useCallback((format: ExportFormat): BuildRequestBodyResult => {
    const collapsedFilters = collapseNoValueFilters(filters);
    const sortField = resolveSortField(sorting, sortFieldByColumn);
    const sort = sortField
      ? [
          {
            field: sortField,
            direction: sorting[0].desc ? SortDirection.DESC : SortDirection.ASC
          }
        ]
      : undefined;
    const search =
      searchTerm && searchTerm.trim().length > 0
        ? { term: searchTerm.trim() }
        : undefined;
    const visibleColumns = getVisibleExportColumns(
      columnsState,
      excludeFromExport
    );
    const columns = visibleColumns.map(
      (col) => exportColumnIdMap?.[col.id] ?? col.id
    );
    // Send the labels the user sees in the UI so the CSV header matches exactly.
    // Backend falls back to the per-account *_fields.label lookup when omitted.
    const labelOverrides = getColumnLabelOverridesRef.current?.();
    const columnLabels = visibleColumns.map(
      (col) => labelOverrides?.[col.id] ?? col.label
    );

    // Use pre-remap IDs so buildColumnValueLabels can match against filter option keys
    // (which are always the original UI column IDs, not the remapped backend field names).
    const visibleColumnIds = new Set(visibleColumns.map((col) => col.id));
    const enumValueLabels = buildColumnValueLabels(
      getFilterOptionsRef.current?.(),
      visibleColumnIds,
      tWithFallback
    );
    const extraValueLabels = getExtraColumnValueLabelsRef.current?.();
    const mergedValueLabels = (() => {
      const merged: Record<string, Record<string, string>> = {
        ...(enumValueLabels ?? {})
      };
      if (extraValueLabels) {
        for (const [colId, map] of Object.entries(extraValueLabels)) {
          if (!visibleColumnIds.has(colId)) continue;
          merged[colId] = { ...(merged[colId] ?? {}), ...map };
        }
      }
      return Object.keys(merged).length > 0 ? merged : undefined;
    })();
    const columnValueLabels = mergedValueLabels;

    // Keyed by the exported column id so the backend matches it against the
    // request's columns; restricted to visible columns the same way.
    const visibleCurrencyFields = columnCurrencyFields
      ? Object.fromEntries(
          Object.entries(columnCurrencyFields)
            .filter(([colId]) => visibleColumnIds.has(colId))
            .map(([colId, field]) => [exportColumnIdMap?.[colId] ?? colId, field])
        )
      : undefined;

    // Refuse to ship an export request without an explicit column list — the
    // backend treats missing/empty `columns` as "default to every model field"
    // and produces a CSV with ~3× the columns the user actually sees. An empty
    // columnsState means the custom-fields query is still in flight (transient,
    // worth waiting out); a populated columnsState with zero visible columns
    // means the user has hidden every column (permanent — nothing to wait for).
    if (columns.length === 0) {
      return columnsState.length === 0
        ? { status: "columns_not_seeded" }
        : { status: "no_visible_columns" };
    }

    const extraFilterParams =
      getExtraFilterParamsRef.current?.(visibleColumnIds);

    return {
      status: "ready",
      body: {
        filter: {
          ...extraFilterParams,
          filters: collapsedFilters,
          ...(search && { search })
        },
        ...(sort && { sort }),
        columns,
        columnLabels,
        ...(columnValueLabels && { columnValueLabels }),
        fullyResolved: true,
        format,
        ...(format === "xlsx" && {
          xlsxOptions: buildXlsxOptions(visibleCurrencyFields)
        })
      }
    };
  }, [
    filters,
    sorting,
    searchTerm,
    columnsState,
    excludeFromExport,
    exportColumnIdMap,
    sortFieldByColumn,
    columnCurrencyFields
  ]);

  const pollUntilReady = useCallback(
    async (jobId: string): Promise<string> => {
      const { intervalMs, maxAttempts, warmupMs } = getPollSchedule(totalRows);
      if (warmupMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, warmupMs));
      }
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
        const status = await pollMutationRef.current.mutateAsync({
          pathParams: { jobId }
        });
        if (status.status === "ready" && status.attachmentId) {
          return status.attachmentId;
        }
        if (status.status === "failed") {
          throw new Error(status.errorCode ?? "export_failed");
        }
      }
      throw new Error("export_timeout");
    },
    [totalRows]
  );

  const downloadAttachment = useCallback(
    async (attachmentId: string, format: ExportFormat): Promise<void> => {
      const { token: tok, tenantId: tid, tokenExpiry: exp } = authRef.current;
      if (exp < Date.now()) throw new Error("TOKEN_EXPIRED");

      const apiCall = getApiEndpoint("attachments/id", {
        pathParams: { attachmentId }
      });
      const url = buildApiUrl(apiCall);

      const response = await axios({
        url,
        method: "GET",
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${tok}`,
          "X-Tenant-Id": tid,
          "ngrok-skip-browser-warning": "69420"
        }
      });

      const contentDisposition =
        (response.headers["content-disposition"] ||
          response.headers["Content-Disposition"] ||
          null) as string | null;
      const filename =
        extractFilenameFromContentDisposition(contentDisposition) ||
        `export.${format}`;

      downloadBlob({ blob: response.data, filename });
    },
    []
  );

  // Resume a still-in-flight job after navigation/reload. The backend dedups
  // re-clicks by filter_hash, so without resume the user has no way to recover
  // a job whose poller was unmounted (CRM-7933).
  useEffect(() => {
    const persisted = readPersistedJob(statusRoute);
    if (!persisted) return;

    let cancelled = false;
    setIsExporting(true);
    (async () => {
      try {
        const attachmentId = await pollUntilReady(persisted.jobId);
        if (cancelled) return;
        await downloadAttachment(attachmentId, persisted.format ?? "csv");
      } catch {
        // Job no longer fetchable (expired, failed, or 404 across tenants).
        // Clear the entry so we don't resume the same dead job on every mount.
      } finally {
        if (!cancelled) {
          clearPersistedJob(statusRoute);
          setIsExporting(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [statusRoute, pollUntilReady, downloadAttachment]);

  const buildRequestBodyRef = useRef(buildRequestBody);
  buildRequestBodyRef.current = buildRequestBody;

  // Columns aren't gating whether the user can click Export anymore — the
  // button stays enabled and this loop waits out the same window
  // buildRequestBody's "columns_not_seeded" result used to abort on
  // immediately (columnsState not yet populated). isExporting is already true
  // for the whole wait, so the existing export-in-progress UI simply runs a
  // bit longer instead of the click silently doing nothing. A genuine
  // zero-visible-columns state is never a transient condition, so it isn't
  // polled — it fails immediately with a distinct error.
  const waitForRequestBody = useCallback(
    async (format: ExportFormat): Promise<ExportRequestBody> => {
    const deadline = Date.now() + COLUMNS_READY_TIMEOUT_MS;
    for (;;) {
      const result = buildRequestBodyRef.current(format);
      if (result.status === "ready") return result.body;
      if (result.status === "no_visible_columns") {
        throw new Error("export_no_visible_columns");
      }
      if (Date.now() >= deadline) throw new Error("export_columns_not_ready");
      await new Promise((resolve) =>
        setTimeout(resolve, COLUMNS_READY_POLL_MS)
      );
    }
    },
    []
  );

  const showExportErrorToast = useCallback((errorCode?: string) => {
    toast.error(
      <ToastBody
        title={
          errorCode === ROW_LIMIT_ERROR_CODE
            ? tWithFallback(
                "exportMenu.rowLimitExceeded",
                "Too many rows for an Excel file. Narrow the filters or export to CSV instead."
              )
            : tWithFallback("exportMenu.exportError", "Export failed")
        }
        actions={
          <IconButton
            size="md"
            variant="iconNoBg"
            iconColor="secondary"
            aria-label={tWithFallback("close", "Close")}
            title={tWithFallback("close", "Close")}
            onClick={() => toast.dismiss()}
          >
            <Close />
          </IconButton>
        }
      />,
      { icon: null }
    );
  }, []);

  const exportTo = useCallback(async (format: ExportFormat) => {
    if (isExporting) return;
    setIsExporting(true);
    trackRef.current(EXPORT_ANALYTICS_EVENT, { format, table: tableName });
    try {
      const body = await waitForRequestBody(format);
      const job = await createMutationRef.current.mutateAsync(body);
      if (!job?.id) throw new Error("No job ID returned");

      // Persist before polling so a navigation mid-poll is recoverable.
      writePersistedJob(statusRoute, job.id, format);

      const attachmentId = await pollUntilReady(job.id);
      await downloadAttachment(attachmentId, format);
    } catch (error) {
      console.error("Export failed:", error);
      showExportErrorToast(error instanceof Error ? error.message : undefined);
    } finally {
      clearPersistedJob(statusRoute);
      setIsExporting(false);
    }
  }, [
    isExporting,
    waitForRequestBody,
    pollUntilReady,
    downloadAttachment,
    statusRoute,
    tableName,
    showExportErrorToast
  ]);

  return { exportTo, isExporting };
};
